import { Element } from './elements/element';
import { ExtensionType } from './elements/extension/extension';
import { EquipmentElements } from './elements/equipmentElements';
import { Figure } from './figures/figure';
import { Rectangle } from './figures/rectangle';
import { konvaSettings } from './konvaSettings';

const STOCK_FILL_COLORS = new Set([
	'#656565',
	'#e0e0e0',
	'#c4c4c4',
	'#c6c6c6',
	'#eaeaea',
	'#eeeeee',
	'grey',
	'gray'
]);

const TYPE_DEFAULT_FILL_COLORS: Partial<Record<EquipmentElements, string>> = {
	[EquipmentElements.BackWall]: konvaSettings.fillWhiteColor,
	[EquipmentElements.CentralPillar]: konvaSettings.fillWhiteColor,
	[EquipmentElements.SideWalls]: konvaSettings.fillGreyColor,
	[EquipmentElements.HookWall]: konvaSettings.fillWhiteColor,
	[EquipmentElements.PerforatedWall]: konvaSettings.fillWhiteColor,
	[EquipmentElements.HookPanel]: konvaSettings.fillWhiteColor,
	[EquipmentElements.Perforation]: konvaSettings.fillWhiteColor,
	[EquipmentElements.Box]: konvaSettings.fillBoxColor,
	[EquipmentElements.SlicedBox]: konvaSettings.fillBoxColor,
	[EquipmentElements.Hook]: konvaSettings.fillGreyColor,
	[EquipmentElements.HangBar]: konvaSettings.fillGreyColor,
	[EquipmentElements.Partition]: konvaSettings.fillGreyColor,
	[EquipmentElements.BaseRectangle]: konvaSettings.fillGreyColor,
	[EquipmentElements.BaseTrapeze]: konvaSettings.fillGreyColor,
	[EquipmentElements.Shelf]: konvaSettings.fillGreyColor,
	[EquipmentElements.AngleShelf]: konvaSettings.fillGreyColor
};

const TYPE_DEFAULT_OUTER_FILL_COLORS: Partial<Record<EquipmentElements, string>> = {
	[EquipmentElements.AngleShelf]: konvaSettings.fillGreyColor
};

export function isStockFillColor(color?: string | null) {
	if (!color) {
		return true;
	}

	return STOCK_FILL_COLORS.has(color.toLowerCase());
}

export function resolveStoredFillColor(stored: string | undefined | null, legacyWhenStock: string) {
	if (!stored || isStockFillColor(stored)) {
		return legacyWhenStock;
	}

	return stored;
}

export function resolveStoredOuterFillColor(stored: string | undefined | null, legacyWhenStock: string) {
	if (!stored || isStockFillColor(stored)) {
		return legacyWhenStock;
	}

	return stored;
}

export function getShapeStoredFillColor(shape?: Pick<Figure, 'color' | 'fillColor'> | null) {
	if (!shape) {
		return undefined;
	}

	const { color, fillColor } = shape;

	if (color != null && fillColor != null && color !== fillColor) {
		const colorIsStock = isStockFillColor(color);
		const fillIsStock = isStockFillColor(fillColor);

		if (!colorIsStock) {
			return color;
		}

		if (!fillIsStock) {
			return fillColor;
		}
	}

	return color ?? fillColor;
}

export function setShapeFillColor(shape: Pick<Figure, 'color' | 'fillColor'>, value: string) {
	shape.color = value;
	shape.fillColor = value;
}

/** Keep legacy `fillColor` in sync when canonical `color` is set. */
export function mirrorFillColorFromColor(shape: Pick<Figure, 'color' | 'fillColor'>) {
	if (shape.color != null && shape.color !== '') {
		shape.fillColor = shape.color;
	}
}

/** Apply persisted shape params; `color` wins and `fillColor` mirrors it when present. */
export function applyShapeFillParamsFromLoad(
	shape: Pick<Figure, 'color' | 'fillColor'>,
	params: { color?: string | null; fillColor?: string | null }
) {
	if (params.fillColor != null) {
		shape.fillColor = params.fillColor;
	}

	if (params.color != null) {
		shape.color = params.color;
	}

	mirrorFillColorFromColor(shape);
}

/** Set canonical `color` from type defaults (or legacy custom fillColor); mirror `fillColor`. */
export function ensureShapeColorForElement(element: Element) {
	const typeDefault = getTypeDefaultFillColor(element.parent.type);

	element.shapes.forEach((shape) => {
		if (shape.color != null && shape.color !== '') {
			mirrorFillColorFromColor(shape);

			return;
		}

		const legacyFill = shape.fillColor;

		if (legacyFill && !isStockFillColor(legacyFill)) {
			setShapeFillColor(shape, legacyFill);

			return;
		}

		setShapeFillColor(shape, typeDefault);
	});
}

export function resolveRenderStrokeColor(
	element: Element,
	legacyWhenStock: string = getTypeDefaultFillColor(element.parent.type),
	detailedView = false
) {
	const stored = getShapeStoredFillColor(element.shapes?.[0]);

	if (!stored || isStockFillColor(stored)) {
		return legacyWhenStock;
	}

	return getElementStrokeColor(element, detailedView);
}

export function getTypeDefaultFillColor(type: EquipmentElements) {
	return TYPE_DEFAULT_FILL_COLORS[type] ?? konvaSettings.fillGreyColor;
}

export function getTypeDefaultOuterFillColor(type: EquipmentElements) {
	return TYPE_DEFAULT_OUTER_FILL_COLORS[type] ?? null;
}

export function getElementFillColor(element: Element | null, shape?: Figure) {
	if (!element) {
		return konvaSettings.fillGreyColor;
	}

	const stored = getShapeStoredFillColor(shape ?? element.shapes?.[0]);

	if (!stored || isStockFillColor(stored)) {
		return getTypeDefaultFillColor(element.parent.type);
	}

	return stored;
}

export function getElementOuterFillColor(element: Element, shape?: Figure) {
	const stored = (shape ?? element.shapes[0])?.outerFillColor;

	if (!stored || isStockFillColor(stored)) {
		const outerDefault = getTypeDefaultOuterFillColor(element.parent.type);

		if (outerDefault) {
			return outerDefault;
		}

		return getElementFillColor(element, shape);
	}

	return stored;
}

export function getHookFillColor(element: Element) {
	const stored = (element.shapes[0] as Rectangle)?.hookFillColor;

	if (!stored || isStockFillColor(stored)) {
		return getTypeDefaultFillColor(EquipmentElements.Hook);
	}

	return stored;
}

export function getElementStrokeColor(element: Element, detailedView = false) {
	const isBoxOrSliced = element.parent.is(EquipmentElements.Box, EquipmentElements.SlicedBox);
	const isMeshBox = isBoxOrSliced && element.isExtensionActive(ExtensionType.Mesh);

	if (isMeshBox || (detailedView && isBoxOrSliced)) {
		return getElementFillColor(element);
	}

	return konvaSettings.strokeColor;
}

const NAMED_COLORS: Record<string, string> = {
	white: '#ffffff',
	grey: '#808080',
	gray: '#808080',
	black: '#000000',
	red: '#ff0000'
};

function parseHexColor(color: string) {
	const hex = NAMED_COLORS[color.toLowerCase()] ?? color;
	const match = /^#([\da-f]{3}|[\da-f]{6})$/i.exec(hex.trim());

	if (!match) {
		return null;
	}

	let value = match[1];

	if (value.length === 3) {
		value = value[0] + value[0] + value[1] + value[1] + value[2] + value[2];
	}

	const num = parseInt(value, 16);

	return {
		r: (num >> 16) & 255,
		g: (num >> 8) & 255,
		b: num & 255
	};
}

export const INACTIVE_EQUIPMENT_STRIPE_PATTERN_SIZE = 24;

const INACTIVE_EQUIPMENT_STRIPE_LINES: ReadonlyArray<readonly [number, number, number, number]> = [
	[2, -2, -2, 2],
	[10, -2, -2, 10],
	[18, -2, -2, 18],
	[26, -2, -2, 26],
	[26, 6, 6, 26],
	[26, 14, 14, 26],
	[26, 22, 22, 26]
];

export function createInactiveEquipmentStripesImage(
	stripeColor: string,
	size = INACTIVE_EQUIPMENT_STRIPE_PATTERN_SIZE
): HTMLCanvasElement {
	const canvas = document.createElement('canvas');

	canvas.width = size;
	canvas.height = size;

	const ctx = canvas.getContext('2d');

	if (!ctx) {
		return canvas;
	}

	ctx.fillStyle = konvaSettings.fillWhiteColor;
	ctx.fillRect(0, 0, size, size);
	ctx.strokeStyle = stripeColor;
	ctx.lineWidth = 1;

	for (const [x1, y1, x2, y2] of INACTIVE_EQUIPMENT_STRIPE_LINES) {
		ctx.beginPath();
		ctx.moveTo(x1, y1);
		ctx.lineTo(x2, y2);
		ctx.stroke();
	}

	return canvas;
}

export function darkenColor(color: string, amount = 0.2) {
	const rgb = parseHexColor(color);

	if (!rgb) {
		return color;
	}

	const factor = Math.max(0, Math.min(1, 1 - amount));
	const toHex = (channel: number) => Math.max(0, Math.min(255, Math.round(channel * factor)))
		.toString(16)
		.padStart(2, '0');

	return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
}

export function lightenColor(color: string, amount = 0.2) {
	const rgb = parseHexColor(color);

	if (!rgb) {
		return color;
	}

	const blend = (channel: number) =>
		Math.max(0, Math.min(255, Math.round(channel + (255 - channel) * amount)));
	const toHex = (channel: number) => blend(channel).toString(16).padStart(2, '0');

	return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
}
